/**
 * useUserSearch hook helper
 * Interface with user profile REST API
 *
 * Author: Relevant Bits
 */

import { useContext } from 'react';

import { useQuery } from '@tanstack/react-query';
import z from 'zod';

import { getAxiosInstance } from './axiosCache';
import { ConfigContext } from '../aurr/contexts/ConfigContext';
import { cacheTTLs } from '../common/constants';
import { logZodError } from '../helpers/log-zod-error';

const userContentSchema = z.object({
  biography: z.string().default(''),
  displayName: z.string(),
  firstName: z.string(),
  id: z.string(),
  lastName: z.string(),
  oxygenId: z.string().optional(),
});
type UserContentSchema = z.infer<typeof userContentSchema>;

interface UseSpeakerDetailsProps {
  oxygenId: string;
  userContentURL?: string;
  bypassCache?: boolean;
  enabled?: boolean;
}

export const useSpeakerDetails = ({
  oxygenId,
  bypassCache = false,
  userContentURL,
  enabled = true,
}: UseSpeakerDetailsProps) => {
  // create axios client used in all community function requests
  const axiosConfig = {
    headers: {
      'Content-Type': 'application/vnd.api+json',
      Accept: 'application/vnd.api+json',
      ...(bypassCache && {
        Pragma: 'no-cache',
        'Cache-Control': 'no-store, max-age=0',
      }),
    },
    ...(!bypassCache && {
      cache: {
        ttl: cacheTTLs.userProfile,
      },
    }),
  };

  const { USER_CONTENT_URL } = useContext(ConfigContext);
  const api = getAxiosInstance(bypassCache);
  const userContentConfig = {
    ...axiosConfig,
    baseURL: userContentURL || USER_CONTENT_URL,
  };

  const {
    isLoading,
    error,
    data = {},
  } = useQuery({
    queryKey: [`userProfile-v1-${oxygenId}`],
    queryFn: () =>
      api
        .get(`usercontent/v1/people/${oxygenId}`, userContentConfig)
        .then(res => {
          logZodError(userContentSchema.safeParse(res.data));

          return res.data;
        }),
    enabled: !!oxygenId && enabled,
  });

  return {
    isLoading,
    error,
    data: data as UserContentSchema,
  };
};
